"use client";

import { c as _c } from "react/compiler-runtime";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import DropCurationButton from "@/components/waves/drops/DropCurationButton";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import WaveDropPartContentMarkdown from "@/components/waves/drops/WaveDropPartContentMarkdown";
import { LinkPreviewProvider } from "@/components/waves/LinkPreviewContext";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useDropCurationMutation } from "@/hooks/drops/useDropCurationMutation";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { matchesDomainOrSubdomain } from "@/lib/url/domains";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import React, { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import WaveLeaderboardGalleryItemVotes from "../gallery/WaveLeaderboardGalleryItemVotes";
import type { WaveLeaderboardGridMode } from "./WaveLeaderboardGrid";
interface WaveLeaderboardGridItemProps {
  readonly drop: ExtendedDrop;
  readonly mode: WaveLeaderboardGridMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MANIFOLD_LISTING_PATH_PATTERN = /^\/@[^/]+\/id\/[^/?#]+\/?$/i;
const SUPER_RARE_ARTWORK_PATH_PATTERN = /^\/artwork\/[^/]+\/0x[a-f0-9]{40}\/[^/?#]+\/?$/i;
const SUPER_RARE_ARTWORK_V2_PATH_PATTERN = /^\/artwork-v2\/[^/?#]+\/?$/i;
const FOUNDATION_MINT_PATH_PATTERN = /^\/mint\/[^/]+\/0x[a-f0-9]{40}\/[^/?#]+\/?$/i;
const OPENSEA_ITEM_PATH_PATTERN = /^\/item\/[^/]+\/0x[a-f0-9]{40}\/[^/?#]+\/?$/i;
const OPENSEA_ASSET_PATH_PATTERN = /^\/assets\/[^/]+\/0x[a-f0-9]{40}\/[^/?#]+\/?$/i;
const TRANSIENT_NFT_PATH_PATTERN = /^\/nfts\/[^/]+\/0x[a-f0-9]{40}\/[^/?#]+\/?$/i;
const getStandaloneHttpUrl = (content: string | null | undefined): string => {
  if (!content) {
    return "";
  }
  const trimmed = content.trim();
  const match = trimmed.match(/^<?(https?:\/\/[^\s<>]+)>?$/i);
  return match?.[1] ?? "";
};
const isMarketplaceLink = (href: string): boolean => {
  try {
    const url = new URL(href);
    const hostname = url.hostname.toLowerCase();
    const pathname = url.pathname;
    if (matchesDomainOrSubdomain(hostname, "manifold.xyz")) {
      return MANIFOLD_LISTING_PATH_PATTERN.test(pathname);
    }
    if (matchesDomainOrSubdomain(hostname, "superrare.com")) {
      return SUPER_RARE_ARTWORK_PATH_PATTERN.test(pathname) || SUPER_RARE_ARTWORK_V2_PATH_PATTERN.test(pathname);
    }
    if (matchesDomainOrSubdomain(hostname, "foundation.app")) {
      return FOUNDATION_MINT_PATH_PATTERN.test(pathname);
    }
    if (matchesDomainOrSubdomain(hostname, "opensea.io")) {
      return OPENSEA_ITEM_PATH_PATTERN.test(pathname) || OPENSEA_ASSET_PATH_PATTERN.test(pathname);
    }
    if (matchesDomainOrSubdomain(hostname, "transient.xyz")) {
      return TRANSIENT_NFT_PATH_PATTERN.test(pathname);
    }
    return false;
  } catch {
    return false;
  }
};
export const WaveLeaderboardGridItem: React.FC<WaveLeaderboardGridItemProps> = t0 => {
  const $ = _c(101);
  const {
    drop,
    mode,
    onDropClick
  } = t0;
  const isCompactMode = mode === "compact";
  const isContentOnlyMode = mode === "content_only";
  const activePart = drop.parts[0];
  const author = drop.author;
  const authorHandle = author?.handle ?? null;
  const primaryMedia = activePart?.media[0];
  const isCuratable = drop.context_profile_context?.curatable ?? false;
  const isCurated = drop.context_profile_context?.curated ?? false;
  const canOpenDrop = drop.drop_type !== ApiDropType.Chat;
  activePart?.content;
  let t1;
  bb0: {
    if (mode !== "content_only" || !!primaryMedia) {
      t1 = false;
      break bb0;
    }
    const standaloneUrl = getStandaloneHttpUrl(activePart?.content);
    if (!standaloneUrl) {
      t1 = false;
      break bb0;
    }
    t1 = isMarketplaceLink(standaloneUrl);
  }
  const isMarketplaceImageOnlyCard = t1;
  let t2;
  bb1: {
    if (isCompactMode) {
      t2 = "tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-0 tw-transition desktop-hover:hover:tw-border-iron-700";
      break bb1;
    }
    if (isMarketplaceImageOnlyCard) {
      t2 = "tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-p-0 tw-transition desktop-hover:hover:tw-border-iron-700";
      break bb1;
    }
    t2 = "tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-p-2 tw-transition desktop-hover:hover:tw-border-iron-700";
  }
  const cardClassName = t2;
  const viewportClassName = isCompactMode ? "tw-relative tw-overflow-hidden tw-bg-iron-950" : "tw-relative tw-overflow-hidden tw-max-h-[20rem]";
  const contentSpacingClass = isCompactMode ? "tw-space-y-3" : "tw-space-y-1";
  const mediaWrapperClass = isCompactMode ? "tw-overflow-hidden tw-rounded-lg tw-bg-iron-900" : "tw-overflow-hidden";
  const isMobileScreen = useIsMobileScreen();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const {
    toggleCuration,
    isPending: isCurating
  } = useDropCurationMutation();
  let t3;
  if ($[0] !== hasTouchScreen) {
    t3 = {
      hasTouchScreen,
      preventDefault: false
    };
    $[0] = hasTouchScreen;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t3);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const [viewportEl, setViewportEl] = useState(null);
  const [innerEl, setInnerEl] = useState(null);
  const hasContentOnlyActions = canOpenDrop || isCuratable || canShowVote;
  const showDesktopContentOnlyActions = isContentOnlyMode && !hasTouchScreen && hasContentOnlyActions;
  const showMobileContentOnlyActions = isContentOnlyMode && hasTouchScreen && hasContentOnlyActions;
  let t4;
  if ($[2] !== drop.metadata) {
    t4 = getDropPreviewImageUrl(drop.metadata);
    $[2] = drop.metadata;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const previewImageUrl = t4;
  const mediaUrl = primaryMedia?.url ?? previewImageUrl ?? null;
  const mediaMimeType = primaryMedia?.mime_type ?? "image/jpeg";
  let t5;
  if ($[4] !== innerEl || $[5] !== viewportEl) {
    t5 = () => {
      if (!viewportEl || !innerEl) {
        return false;
      }
      return innerEl.scrollHeight > viewportEl.clientHeight + 1;
    };
    $[4] = innerEl;
    $[5] = viewportEl;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const getOverflowSnapshot = t5;
  let t6;
  if ($[7] !== innerEl || $[8] !== viewportEl) {
    t6 = onStoreChange => {
      if (!viewportEl || !innerEl) {
        return _temp;
      }
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", onStoreChange);
        return () => {
          window.removeEventListener("resize", onStoreChange);
        };
      }
      const observer = new ResizeObserver(() => {
        onStoreChange();
      });
      observer.observe(viewportEl);
      observer.observe(innerEl);
      return () => {
        observer.disconnect();
      };
    };
    $[7] = innerEl;
    $[8] = viewportEl;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const subscribeToOverflow = t6;
  const showGradient = useSyncExternalStore(subscribeToOverflow, getOverflowSnapshot, _temp2);
  const hasUserVoted = drop.context_profile_context?.rating !== undefined;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const isNegativeVote = userVote < 0;
  const isZeroVote = userVote === 0;
  let voteStyle = "tw-text-iron-300";
  if (isZeroVote || isNegativeVote) {
    voteStyle = "tw-text-iron-400";
  }
  const votingCreditType = drop.wave.voting_credit_type;
  const votingCreditLabels = WAVE_VOTING_LABELS as Partial<Record<typeof votingCreditType, string>>;
  const votingCreditLabel = votingCreditLabels[votingCreditType] ?? votingCreditType;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsVotingModalOpen(true);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleVoteButtonClick = t7;
  let t8;
  if ($[11] !== drop.id || $[12] !== drop.wave.id || $[13] !== isCuratable || $[14] !== isCurated || $[15] !== setIsActive || $[16] !== toggleCuration) {
    t8 = () => {
      if (!isCuratable || drop.id.startsWith("temp-")) {
        return;
      }
      toggleCuration({
        dropId: drop.id,
        waveId: drop.wave.id,
        isCuratable,
        isCurated
      });
      setIsActive(false);
    };
    $[11] = drop.id;
    $[12] = drop.wave.id;
    $[13] = isCuratable;
    $[14] = isCurated;
    $[15] = setIsActive;
    $[16] = toggleCuration;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleMobileCurateClick = t8;
  let t9;
  if ($[18] !== setIsActive) {
    t9 = () => {
      setIsActive(false);
      setIsVotingModalOpen(true);
    };
    $[18] = setIsActive;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleMobileVoteClick = t9;
  let t10;
  if ($[20] !== drop || $[21] !== isMobileScreen || $[22] !== onDropClick) {
    t10 = () => {
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_grid",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[20] = drop;
    $[21] = isMobileScreen;
    $[22] = onDropClick;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const openDrop = t10;
  let t11;
  if ($[24] !== isActive || $[25] !== openDrop || $[26] !== showMobileContentOnlyActions) {
    t11 = event => {
      if (showMobileContentOnlyActions && isActive) {
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest("a, button")) {
        return;
      }
      openDrop();
    };
    $[24] = isActive;
    $[25] = openDrop;
    $[26] = showMobileContentOnlyActions;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const onCardClick = t11;
  let t12;
  if ($[28] !== openDrop) {
    t12 = event_0 => {
      if (event_0.key === "Enter" || event_0.key === " ") {
        event_0.preventDefault();
        openDrop();
      }
    };
    $[28] = openDrop;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const onCardKeyDown = t12;
  const t13 = `wave-leaderboard-grid-item-${drop.id}`;
  const t14 = `${cardClassName} tw-group`;
  let t15;
  if ($[30] !== showMobileContentOnlyActions || $[31] !== touchHandlers) {
    t15 = showMobileContentOnlyActions ? touchHandlers : {};
    $[30] = showMobileContentOnlyActions;
    $[31] = touchHandlers;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== mediaMimeType || $[34] !== mediaUrl || $[35] !== mediaWrapperClass || $[36] !== previewImageUrl) {
    t16 = mediaUrl && <div className={mediaWrapperClass}><MediaDisplay media_mime_type={mediaMimeType} media_url={mediaUrl} disableMediaInteraction={true} imageScale={ImageScale.AUTOx450} previewImageUrl={previewImageUrl} /></div>;
    $[33] = mediaMimeType;
    $[34] = mediaUrl;
    $[35] = mediaWrapperClass;
    $[36] = previewImageUrl;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== activePart || $[39] !== drop.mentioned_users || $[40] !== drop.mentioned_waves || $[41] !== drop.referenced_nfts || $[42] !== drop.wave || $[43] !== mode) {
    t17 = activePart && <LinkPreviewProvider variant="home"><WaveDropPartContentMarkdown mentionedUsers={drop.mentioned_users} mentionedWaves={drop.mentioned_waves} referencedNfts={drop.referenced_nfts} part={activePart} wave={drop.wave} onQuoteClick={_temp3} marketplaceImageOnly={mode === "content_only"} /></LinkPreviewProvider>;
    $[38] = activePart;
    $[39] = drop.mentioned_users;
    $[40] = drop.mentioned_waves;
    $[41] = drop.referenced_nfts;
    $[42] = drop.wave;
    $[43] = mode;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== contentSpacingClass || $[46] !== t16 || $[47] !== t17) {
    t18 = <div ref={setInnerEl} className={contentSpacingClass}>{t16}{t17}</div>;
    $[45] = contentSpacingClass;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== showGradient) {
    t19 = showGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-900 tw-via-iron-900/90 tw-to-transparent" />;
    $[49] = showGradient;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== canOpenDrop || $[52] !== canShowVote || $[53] !== drop || $[54] !== isCuratable || $[55] !== isCurated || $[56] !== showDesktopContentOnlyActions) {
    t20 = showDesktopContentOnlyActions && <div data-testid={`wave-leaderboard-grid-item-content-only-actions-${drop.id}`} className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-0 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/65 tw-to-transparent tw-p-2 tw-opacity-0 tw-transition-opacity tw-duration-200 group-focus-within:tw-opacity-100 desktop-hover:group-hover:tw-opacity-100"><div className="tw-pointer-events-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">{canOpenDrop && <div className="tw-flex tw-h-8 tw-items-center"><WaveDropActionsOpen drop={drop} /></div>}{isCuratable && <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={isCuratable} isCurated={isCurated} className="tw-bg-iron-950/70" />}{canShowVote && <VotingModalButton drop={drop} onClick={handleVoteButtonClick} variant="subtle" />}</div></div>;
    $[51] = canOpenDrop;
    $[52] = canShowVote;
    $[53] = drop;
    $[54] = isCuratable;
    $[55] = isCurated;
    $[56] = showDesktopContentOnlyActions;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== viewportClassName) {
    t21 = <div ref={setViewportEl} className={viewportClassName}>{t18}{t19}{t20}</div>;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = viewportClassName;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== authorHandle || $[64] !== canShowVote || $[65] !== drop || $[66] !== hasUserVoted || $[67] !== isCompactMode || $[68] !== isNegativeVote || $[69] !== userVote || $[70] !== voteStyle || $[71] !== votingCreditLabel) {
    t22 = isCompactMode && <div data-testid={`wave-leaderboard-grid-item-footer-${drop.id}`} className="tw-rounded-b-lg tw-bg-iron-950/50 tw-px-3 tw-pb-3"><div className="tw-mb-1.5">{drop.title && <h3 className="tw-mb-0 tw-truncate tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-200">{drop.title}</h3>}<div className="tw-flex tw-items-center tw-justify-between"><div className="tw-mr-2 tw-min-w-0 tw-flex-1">{authorHandle && <UserProfileTooltipWrapper user={authorHandle}><Link onClick={_temp4} href={`/${authorHandle}`} className="tw-text-xs tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-150 desktop-hover:hover:tw-text-iron-300 desktop-hover:hover:tw-underline">{authorHandle}</Link></UserProfileTooltipWrapper>}</div>{drop.rank !== null ? <WinnerDropBadge rank={drop.rank} decisionTime={drop.winning_context?.decision_time ?? null} /> : <div className="tw-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded-xl tw-bg-iron-800 tw-px-2 tw-text-xs tw-font-semibold tw-text-iron-400">-</div>}</div></div><div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-text-xs"><WaveLeaderboardGalleryItemVotes drop={drop} variant="subtle" /><div className="tw-ml-4 tw-flex tw-items-center tw-gap-1 tw-text-iron-500"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="tw-size-3 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" /></svg><span className="tw-font-medium">{formatNumberWithCommas(drop.raters_count)}</span></div></div><div className="tw-flex tw-items-center tw-justify-between tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-pt-2">{hasUserVoted && <span className="tw-font-mono tw-text-[11px] tw-text-iron-500">You:{" "}<span className={voteStyle}>{isNegativeVote && "-"}{formatNumberWithCommas(Math.abs(userVote))}{" "}{votingCreditLabel}</span></span>}<div className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5"><DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={drop.context_profile_context?.curatable ?? false} isCurated={drop.context_profile_context?.curated ?? false} />{canShowVote && <VotingModalButton drop={drop} onClick={handleVoteButtonClick} variant="subtle" />}</div></div></div>;
    $[63] = authorHandle;
    $[64] = canShowVote;
    $[65] = drop;
    $[66] = hasUserVoted;
    $[67] = isCompactMode;
    $[68] = isNegativeVote;
    $[69] = userVote;
    $[70] = voteStyle;
    $[71] = votingCreditLabel;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== drop || $[74] !== isCompactMode || $[75] !== isContentOnlyMode || $[76] !== isMobileScreen || $[77] !== isVotingModalOpen) {
    t23 = (isCompactMode || isContentOnlyMode) && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />);
    $[73] = drop;
    $[74] = isCompactMode;
    $[75] = isContentOnlyMode;
    $[76] = isMobileScreen;
    $[77] = isVotingModalOpen;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== canOpenDrop || $[80] !== canShowVote || $[81] !== drop || $[82] !== handleMobileCurateClick || $[83] !== handleMobileVoteClick || $[84] !== isActive || $[85] !== isCuratable || $[86] !== isCurated || $[87] !== isCurating || $[88] !== setIsActive || $[89] !== showMobileContentOnlyActions) {
    t24 = showMobileContentOnlyActions && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={setIsActive}><div className="tw-grid tw-grid-cols-1 tw-gap-y-2">{canOpenDrop && <WaveDropMobileMenuOpen drop={drop} onOpenChange={() => setIsActive(false)} />}{isCuratable && <button type="button" onClick={handleMobileCurateClick} disabled={isCurating || drop.id.startsWith("temp-")} className={`tw-flex tw-select-none tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 tw-text-left tw-transition-colors tw-duration-200 ${isCurating || drop.id.startsWith("temp-") ? "tw-cursor-default tw-opacity-60" : "active:tw-bg-iron-800"}`}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg"><path d="M8 12H16M8 16H13M8.6 21H15.4C16.5201 21 17.0802 21 17.508 20.782C17.8843 20.5903 18.1903 20.2843 18.382 19.908C18.6 19.4802 18.6 18.9201 18.6 17.8V8.2C18.6 7.07989 18.6 6.51984 18.382 6.09202C18.1903 5.71569 17.8843 5.40973 17.508 5.21799C17.0802 5 16.5201 5 15.4 5H8.6C7.47989 5 6.91984 5 6.49202 5.21799C6.11569 5.40973 5.80973 5.71569 5.61799 6.09202C5.4 6.51984 5.4 7.07989 5.4 8.2V17.8C5.4 18.9201 5.4 19.4802 5.61799 19.908C5.80973 20.2843 6.11569 20.5903 6.49202 20.782C6.91984 21 7.47989 21 8.6 21ZM15.75 3V7.5M13.5 5.25H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><span className="tw-text-base tw-font-semibold tw-text-iron-300">{isCurated ? "Uncurate drop" : "Curate drop"}</span></button>}{canShowVote && <button type="button" onClick={handleMobileVoteClick} className="tw-flex tw-select-none tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 tw-text-left tw-transition-colors tw-duration-200 active:tw-bg-iron-800"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg"><path d="M12 2.75L14.8379 8.50092L21.1845 9.42395L16.5923 13.8991L17.6765 20.221L12 17.2379L6.32352 20.221L7.4077 13.8991L2.8155 9.42395L9.16215 8.50092L12 2.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><span className="tw-text-base tw-font-semibold tw-text-iron-300">Vote</span></button>}</div></CommonDropdownItemsMobileWrapper>, document.body);
    $[79] = canOpenDrop;
    $[80] = canShowVote;
    $[81] = drop;
    $[82] = handleMobileCurateClick;
    $[83] = handleMobileVoteClick;
    $[84] = isActive;
    $[85] = isCuratable;
    $[86] = isCurated;
    $[87] = isCurating;
    $[88] = setIsActive;
    $[89] = showMobileContentOnlyActions;
    $[90] = t24;
  } else {
    t24 = $[90];
  }
  let t25;
  if ($[91] !== onCardClick || $[92] !== onCardKeyDown || $[93] !== t13 || $[94] !== t14 || $[95] !== t15 || $[96] !== t21 || $[97] !== t22 || $[98] !== t23 || $[99] !== t24) {
    t25 = <div role="button" tabIndex={0} data-testid={t13} onClick={onCardClick} onKeyDown={onCardKeyDown} className={t14} {...t15}>{t21}{t22}{t23}{t24}</div>;
    $[91] = onCardClick;
    $[92] = onCardKeyDown;
    $[93] = t13;
    $[94] = t14;
    $[95] = t15;
    $[96] = t21;
    $[97] = t22;
    $[98] = t23;
    $[99] = t24;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  return t25;
};
function _temp() {}
function _temp2() {
  return false;
}
function _temp3() {}
function _temp4(e) {
  return e.stopPropagation();
}